import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { CheckCircle2 } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { eventBus } from '@/events/eventBus';
import HostedPaymentMock from '@/components/HostedPaymentMock';
import { CustomerShell } from '@/screens/customer-flow/SelfBookPage';
import { payTopupInstalment } from '@/domain/topupBuilderFlow';
import { topupOutstandingAud } from '@/domain/topupPlan';
import { aud } from '@/utils/format';
import type { TopupInstalment, TopupPlan } from '@/domain/types';

// Customer-facing tap-to-pay page for a pay-over-time plan (tv-1a9). Mirrors the
// self-book link page: an UNGUARDED customer route, wrapped in the same shell,
// with a hosted-payment handoff. It reads the plan straight from the store, so a
// paid instalment (which upserts the plan) re-renders the page to the next
// instalment or the paid-in-full done state. Reuses tv-w98's outstanding helper.

type InstalmentUiState = 'paid' | 'due' | 'upcoming';

/** The earliest instalment still owed (scheduled / reminded / missed). */
function nextUnpaidIndex(plan: TopupPlan): number {
  return plan.instalments.findIndex(
    (i) => i.status === 'scheduled' || i.status === 'reminded' || i.status === 'missed',
  );
}

function instalmentUiState(inst: TopupInstalment, index: number, dueIndex: number): InstalmentUiState {
  if (inst.status === 'paid') return 'paid';
  if (index === dueIndex) return 'due';
  return 'upcoming';
}

export default function TopupPayPage() {
  const { planId } = useParams<{ planId: string }>();
  const plan = useBrainStore((s) => (planId ? s.topupPlans[planId] : undefined));

  // A server-backed pay that the atomic intent refuses (tv-rp7.4). This route
  // renders outside AppShell, so a toast is invisible here — the pay flow itself
  // must show the failure and reset the hosted-payment mock out of its optimistic
  // "confirmed" state (a remount via the bumped key), so the customer retries
  // rather than being stranded on a false success. Nothing was charged.
  const [payError, setPayError] = useState<string | null>(null);
  const [payAttempt, setPayAttempt] = useState(0);

  // Listen for a refused pay of THIS plan's deposit and surface it in the flow.
  const depositId = plan?.depositId;
  useEffect(() => {
    if (!depositId) return;
    return eventBus.subscribe('stripe-webhook-failed', (p) => {
      if ((p as { depositId?: string } | undefined)?.depositId === depositId) {
        setPayError("Your payment didn't go through — no charge was made. Please try again.");
        setPayAttempt((n) => n + 1); // remount HostedPaymentMock back to its idle state
      }
    });
  }, [depositId]);

  if (!plan) {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center">
          This payment plan link has expired or was not generated yet. Get in touch with the studio.
        </p>
      </CustomerShell>
    );
  }

  if (plan.status === 'cancelled') {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center">
          This payment plan has been cancelled. Get in touch with the studio to sort out your booking.
        </p>
      </CustomerShell>
    );
  }

  const outstanding = topupOutstandingAud(plan);
  const dueIndex = nextUnpaidIndex(plan);
  const nextDue = dueIndex === -1 ? null : plan.instalments[dueIndex]!;

  // ---- DONE (paid in full) -------------------------------------------------
  if (plan.status === 'completed' || nextDue === null) {
    return (
      <CustomerShell>
        <div className="text-center py-8" data-testid="topup-done">
          <CheckCircle2 size={48} className="text-mint mx-auto" />
          <h1 className="heading-display text-2xl text-cream mt-4">Paid in full</h1>
          <p className="font-serif text-cream-soft mt-2">
            Every instalment is settled. Your deposit is fully paid and your booking is secure.
          </p>
        </div>
      </CustomerShell>
    );
  }

  const paidCount = plan.instalments.filter((i) => i.status === 'paid').length;

  return (
    <CustomerShell>
      <p className="label-small-caps text-gold-bright text-center">Your deposit plan</p>
      <h1 className="heading-display text-2xl text-cream text-center mt-1" data-testid="topup-outstanding">
        {aud(outstanding)} to go
      </h1>
      <p className="font-serif text-cream-soft text-center mt-2">
        Next instalment{' '}
        <span className="text-gold-bright" data-testid="topup-next-due">
          {aud(nextDue.amountAud)}
        </span>{' '}
        due {format(new Date(nextDue.dueIso), 'EEE d MMM')}.
      </p>

      <div className="mt-6" data-testid="topup-schedule">
        <p className="label-small-caps text-gold-bright mb-2">
          Schedule · {paidCount} of {plan.instalments.length} paid
        </p>
        <ul className="space-y-1">
          {plan.instalments.map((inst, i) => {
            const state = instalmentUiState(inst, i, dueIndex);
            return (
              <li
                key={inst.dueIso}
                data-testid={`topup-row-${i}`}
                data-state={state}
                className="flex items-center justify-between rounded-md border border-gold-dim/30 px-3 py-2 font-serif text-sm text-cream"
              >
                <span>{format(new Date(inst.dueIso), 'EEE d MMM')}</span>
                <span className="flex items-center gap-2">
                  <span className="tabular-nums">{aud(inst.amountAud)}</span>
                  <span
                    className={
                      state === 'paid'
                        ? 'font-inter text-[9px] uppercase tracking-widest text-mint'
                        : state === 'due'
                          ? 'font-inter text-[9px] uppercase tracking-widest text-gold-bright'
                          : 'font-inter text-[9px] uppercase tracking-widest text-cream-soft'
                    }
                  >
                    {state === 'paid' ? 'Paid' : state === 'due' ? 'Due now' : 'Upcoming'}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mt-6">
        {payError ? (
          <p
            className="mb-3 rounded-md border border-blood/50 bg-blood/10 p-3 text-center font-serif text-sm text-cream"
            data-testid="topup-pay-error"
          >
            {payError}
          </p>
        ) : null}
        <HostedPaymentMock
          key={`topup-pay-${dueIndex}-${payAttempt}`}
          amountAud={nextDue.amountAud}
          description={`Instalment ${paidCount + 1} of ${plan.instalments.length} on your deposit plan.`}
          testid="topup-hosted-payment"
          // 7ktj: "Payment confirmed" only once the server took it — never a
          // success flash that a refusal then takes back.
          settle={() =>
            new Promise<boolean>((resolve) => {
              setPayError(null); // clear any prior failure as the retry begins
              if (!planId) return resolve(false);
              const started = payTopupInstalment(planId, { onSettled: resolve });
              if (!started.ok) resolve(false);
            })
          }
          onConfirm={() => {}}
        />
      </div>
    </CustomerShell>
  );
}
